import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ISSUE_STATES, createMilestone, listMilestones, listRepositories, updateMilestone } from '@mkcore/dev/schema';
import { Badge, Button, Card, EmptyState, Field, Input, NativeSelect, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { useActionMutation } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { connectionsQuery } from '@/features/admin/connections-panel';

const search = z.object({ repositoryId: z.string().optional(), state: z.enum(ISSUE_STATES).optional() });
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_app/dev/milestones')({ validateSearch: search, component: Milestones });

const reposQuery = queryOptions({ queryKey: ['dev', 'repo', 'list', 'enabled'], queryFn: () => call(listRepositories, { enabled: true }) });
/** A date input (YYYY-MM-DD) as the start of that day in UTC, the way Forgejo keeps a due date. */
const toDue = (d: string) => (d ? `${d}T00:00:00.000Z` : null);

/**
 * Milestones of a mirrored repository with their progress from the mirrored issues (CO-0388). With a linked Forgejo
 * account you add one, close or reopen it and move its due date; the change goes to Forgejo first.
 */
function Milestones() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const repos = useQuery(reposQuery);
  const repositoryId = s.repositoryId ?? repos.data?.items[0]?.id;
  const state = s.state ?? 'open';
  const q = useQuery({
    queryKey: ['dev', 'milestone', 'list', repositoryId, state],
    queryFn: () => call(listMilestones, { repositoryId: repositoryId!, state }),
    enabled: !!repositoryId,
  });
  const conn = useQuery(connectionsQuery);
  const linked = conn.data?.items.some((c) => c.provider === 'forgejo' && c.status === 'active') ?? false;
  const canUpdate = useCan('dev:update') && linked;
  const canCreate = useCan('dev:create') && linked;
  const update = useActionMutation(updateMilestone, { invalidate: [['dev', 'milestone']] });
  return (
    <>
      <PageHeader eyebrow={t('nav.dev')} title={t('milestones.title')} description={t('milestones.description')} />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('issues.repository')} className="w-64">
          {(p) => (
            <NativeSelect {...p} value={repositoryId ?? ''} onChange={(e) => set({ repositoryId: e.target.value || undefined })}>
              {(repos.data?.items ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.fullName}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('common.status')} className="w-36">
          {(p) => (
            <NativeSelect {...p} value={state} onChange={(e) => set({ state: e.target.value as Search['state'] })}>
              {ISSUE_STATES.map((st) => (
                <option key={st} value={st}>
                  {t(`issues.state.${st}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
      </div>
      {canCreate && repositoryId && <NewMilestone repositoryId={repositoryId} />}
      {repos.data && !repos.data.items.length ? (
        <EmptyState title={t('milestones.noRepositories')} />
      ) : !q.data ? (
        <Skeleton className="h-48" />
      ) : !q.data.items.length ? (
        <EmptyState title={t('milestones.empty')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('milestones.milestone')}</Th>
              <Th>{t('milestones.due')}</Th>
              <Th className="w-48">{t('milestones.progress')}</Th>
              <Th>{t('common.status')}</Th>
              {canUpdate && <Th className="text-right">{t('common.actions')}</Th>}
            </Tr>
          </THead>
          <TBody>
            {q.data.items.map((m) => {
              const pct = Math.round(m.progress * 100);
              return (
                <Tr key={m.id}>
                  <Td>
                    <Link to="/dev/milestones/$id" params={{ id: m.id }} className="font-bold hover:underline">
                      {m.title}
                    </Link>
                    {m.description && <div className="text-xs text-fg-3">{m.description}</div>}
                  </Td>
                  <Td className="tabular">
                    {canUpdate ? (
                      <Input
                        type="date"
                        aria-label={t('milestones.dueOf', { title: m.title })}
                        className="w-40"
                        defaultValue={m.dueOn?.slice(0, 10) ?? ''}
                        onBlur={(e) => {
                          const next = toDue(e.target.value);
                          if ((next ?? '') !== (m.dueOn ?? '')) update.mutate({ input: { id: m.id, dueOn: next } });
                        }}
                      />
                    ) : m.dueOn ? (
                      formatDate(m.dueOn, 'UTC')
                    ) : (
                      '—'
                    )}
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2" title={t('milestones.counts', { closed: m.closedIssues, open: m.openIssues })}>
                      <div className="h-2 flex-1 rounded-full bg-sunken" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={t('milestones.progressOf', { title: m.title })}>
                        <div className="h-2 rounded-full bg-success" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-10 text-right text-xs tabular">{pct}%</span>
                    </div>
                  </Td>
                  <Td>
                    <Badge tone={m.state === 'open' ? 'success' : 'neutral'}>{t(`issues.state.${m.state}`)}</Badge>
                  </Td>
                  {canUpdate && (
                    <Td className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={update.isPending && update.variables?.input.id === m.id}
                        onClick={() => update.mutate({ input: { id: m.id, state: m.state === 'open' ? 'closed' : 'open' } })}
                      >
                        {m.state === 'open' ? t('issues.close') : t('issues.reopen')}
                      </Button>
                    </Td>
                  )}
                </Tr>
              );
            })}
          </TBody>
        </Table>
      )}
    </>
  );
}

function NewMilestone({ repositoryId }: { repositoryId: string }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const create = useActionMutation(createMilestone, {
    invalidate: [['dev', 'milestone']],
    success: t('milestones.created'),
    onSuccess: () => {
      setTitle('');
      setDue('');
    },
  });
  return (
    <Card className="mb-4 p-4">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate({ input: { repositoryId, title, dueOn: toDue(due) } });
        }}
      >
        <Field label={t('milestones.newTitle')} className="w-64">
          {(p) => <Input {...p} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={255} />}
        </Field>
        <Field label={t('milestones.due')} className="w-44">
          {(p) => <Input {...p} type="date" value={due} onChange={(e) => setDue(e.target.value)} />}
        </Field>
        <Button type="submit" loading={create.isPending} disabled={!title.trim()}>
          {t('milestones.create')}
        </Button>
      </form>
    </Card>
  );
}
